import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FullWidthPageContent } from 'layouts/SingleColumnLayout';
import InfoBox from 'components/widget/InfoBox';
import Button from 'components/Button';
import BfpoAddress from 'components/BfpoAddress';
import DefaultAddress from 'components/DefaultAddress';
import UKAddress from 'components/UKAddress';
import Checkbox, { CheckboxesArea } from 'components/Checkbox';
import {
  MAKE_THIS_MY_DELIVERY_MULTIPLE_SUB_TEXT,
  MAKE_THIS_MY_DELIVERY_SINGLE_SUB_TEXT,
  MAKE_THIS_MY_PRIMARY_ADDRESS_TEXT,
  MAKE_THIS_MY_PRIMARY_ADDRESS_TITLE,
  MAKE_THIS_MY_DELIVERY_TITLE,
  SAVE_ADDRESS_CHANGE_DELAYED_TEXT,
  ADD_NEW_ADDRESS_INFO,
  COUNTRY_IE,
  COUNTRY_UK,
  COUNTRY_BFPO,
} from 'helpers/copyHelper';
import Select from 'components/Select';
import { getDisplayCountriesFromTaxCode } from 'helpers/addressHelper';

const DeliveryInfoBox = styled(InfoBox)`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.palette.text.light};
`;

const Wrapper = styled(FullWidthPageContent)`
  > header {
    padding: ${({ theme }) => theme.spacing * 2}px;
  }

  .contentWrapper {
    > div {
      margin-bottom: ${({ theme }) => theme.spacing * 3}px;
    }

    background-color: ${({ theme }) => theme.palette.background.paper};
    padding: ${({ theme }) => theme.spacing * 2}px;
  }

  .formCtas {
    padding: ${({ theme }) => theme.spacing * 2}px;
    .Button {
      margin-bottom: ${({ theme }) => theme.spacing * 2}px;
    }
  }

  .changeDelayedText {
    margin: 0 0 0 ${({ theme }) => theme.spacing}px;
  }

  .ROIOnlyInfo {
    margin-bottom: ${({ theme }) => theme.spacing * 2}px;
  }
`;

const AddAddressView = ({
  className, allowDefaultAddress, allowAllFutureChange, multipleSubs, onCancel, onSubmit,
  changeDelayed, errors, onChange, userStoreTaxRegion,
}) => {
  const classes = ['AddAddressView'];
  if (className) classes.push(className);
  const [formValue, setFormValue] = useState({});
  const [validity, setValidity] = useState(false);
  const [allFutureDeliveries, setAllFutureDeliveries] = useState(false);
  const [primaryAddress, setPrimaryAddress] = useState(false);
  const [pendingConfirmed, setPendingConfirmed] = useState(false);
  const [country, setCountry] = useState();

  React.useEffect(() => {
    setCountry(userStoreTaxRegion === 'IEH' ? COUNTRY_IE : COUNTRY_UK);
  }, [userStoreTaxRegion, setCountry]);

  React.useEffect(() => {
    setPendingConfirmed(changeDelayed);
  }, [changeDelayed, setPendingConfirmed]);

  function handleCountryChange(e) {
    setFormValue({});
    setCountry(e.target.value);
  }

  function setAddress(updates) {
    setFormValue({ ...formValue, ...updates });
    if (onChange) onChange(updates);
  }

  function handleAllFutureChange(e) {
    setAllFutureDeliveries(e.target.checked);
    setPendingConfirmed(false);
  }

  function handleCancel() {
    if (onCancel) onCancel();
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (onSubmit) {
      onSubmit({
        country,
        county: formValue.county,
        town_city: formValue.town,
        house_flat_number: formValue.houseNo,
        house_name: formValue.houseName,
        street: formValue.address,
        postcode: formValue.postcode,
      }, primaryAddress, allFutureDeliveries);
    }
  }

  let Address;
  switch (country) {
    case COUNTRY_UK: Address = UKAddress; break;
    case COUNTRY_BFPO: Address = BfpoAddress; break;
    default: Address = DefaultAddress; break;
  }

  return (
    <Wrapper className={classes.join(' ')}>
      <header>
        <h1>Add a new address</h1>
        <p>Add your delivery or primary addresses</p>
        <DeliveryInfoBox>
          {ADD_NEW_ADDRESS_INFO(userStoreTaxRegion)}
        </DeliveryInfoBox>
      </header>
      <form id="addAddressForm" onSubmit={handleSubmit}>
        <div className="contentWrapper">
          <Select
            name="country"
            label="Country"
            options={getDisplayCountriesFromTaxCode(userStoreTaxRegion)}
            placeholder="Required"
            onChange={handleCountryChange}
            value={country}
            error={errors && errors.country}
          />

          <Address errors={errors} onChange={setAddress} onValidity={setValidity} />

          <CheckboxesArea>
            {allowAllFutureChange && (
              <Checkbox
                onChange={handleAllFutureChange}
                className="allFutureChangeChkBox"
                dataLayer={{ action: 'Make This My Delivery Address Click' }}
              >
                <h4>{MAKE_THIS_MY_DELIVERY_TITLE()}</h4>
                <p>
                  {multipleSubs
                    ? MAKE_THIS_MY_DELIVERY_MULTIPLE_SUB_TEXT()
                    : MAKE_THIS_MY_DELIVERY_SINGLE_SUB_TEXT()}
                </p>
              </Checkbox>
            )}
            {allowDefaultAddress && (
              <Checkbox onChange={(e) => setPrimaryAddress(e.target.checked)} className="defaultAddressChkBox">
                <h4>{MAKE_THIS_MY_PRIMARY_ADDRESS_TITLE()}</h4>
                <p>
                  {MAKE_THIS_MY_PRIMARY_ADDRESS_TEXT()}
                </p>
              </Checkbox>
            )}
            {changeDelayed && allowAllFutureChange && (
              <Checkbox
                checked={(pendingConfirmed && allFutureDeliveries)}
                onChange={(e) => setPendingConfirmed(e.target.checked)}
                disabled={!allFutureDeliveries}
                dataLayer={{ action: 'Make This My Primary Address Click' }}
              >
                <p className="changeDelayedText">{SAVE_ADDRESS_CHANGE_DELAYED_TEXT()}</p>
              </Checkbox>
            )}
          </CheckboxesArea>
        </div>

        <div className="formCtas">
          <Button
            disabled={!validity || (allFutureDeliveries && changeDelayed && !pendingConfirmed)}
            fullWidth
            type="submit"
            form="addAddressForm"
            dataLayer={{ action: 'Save Address Click', label: 'Save Address' }}
          >
            Save address
          </Button>
          <Button
            inverse
            fullWidth
            type="button"
            onClick={handleCancel}
            dataLayer={{ action: 'Cancel Address Change Click', label: 'Cancel Address Change' }}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Wrapper>
  );
};

AddAddressView.displayName = 'AddAddressView';

AddAddressView.propTypes = {
  className: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  userStoreTaxRegion: PropTypes.oneOf(['GYH', 'JEH', 'IEH', 'GBH']).isRequired,
  onChange: PropTypes.func,
  allowDefaultAddress: PropTypes.bool,
  allowAllFutureChange: PropTypes.bool,
  multipleSubs: PropTypes.bool,
  changeDelayed: PropTypes.bool,
  errors: PropTypes.shape({
    country: PropTypes.string,
  }),
};

AddAddressView.defaultProps = {
  className: null,
  allowDefaultAddress: false,
  allowAllFutureChange: false,
  multipleSubs: false,
  changeDelayed: false,
  errors: null,
  onChange: null,
};

export default AddAddressView;
